import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Smartphone, ArrowRight, AlertCircle, User } from "lucide-react";

export default function EmployeeLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", { email, password });
      
      // Verify user is an employee
      if (data.role !== "employee") {
        setError("This portal is for caregivers only. Please use Staff Login.");
        setLoading(false);
        return;
      }
      
      setUser(data);
      navigate("/employee/workspace");
    } catch (err) {
      // Handle error - detail can be string or array
      let msg = "Login failed";
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === "string") {
          msg = detail;
        } else if (Array.isArray(detail) && detail[0]?.msg) {
          msg = detail[0].msg;
        }
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#06372B] via-[#0a4a3a] to-[#06372B] flex flex-col items-center justify-center p-4">
      {/* Mobile-first card */}
      <div className="w-full max-w-sm">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur mb-4">
            <Smartphone className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Caregiver Portal</h1>
          <p className="text-white/70 text-sm mt-1">Sign in to access your dashboard</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 space-y-4">
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
              autoComplete="email"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#06372B] focus:ring-2 focus:ring-[#06372B]/20 outline-none transition-all text-base"
              data-testid="employee-login-email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#06372B] focus:ring-2 focus:ring-[#06372B]/20 outline-none transition-all text-base"
              data-testid="employee-login-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 px-4 rounded-xl bg-[#06372B] text-white font-semibold text-base flex items-center justify-center gap-2 hover:bg-[#0a4a3a] transition-colors disabled:opacity-60"
            data-testid="employee-login-submit"
          >
            {loading ? (
              <span className="animate-pulse">Signing in...</span>
            ) : (
              <>Sign In <ArrowRight className="w-4 h-4" /></>
            )}
          </button>

          <div className="text-center">
            <Link 
              to="/forgot-password" 
              className="text-sm text-[#06372B] hover:underline"
            >
              Forgot password?
            </Link>
          </div>
        </form>

        {/* Staff login link */}
        <div className="mt-6 text-center">
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"
          >
            <User className="w-4 h-4" />
            Staff / Admin Login
          </Link>
        </div>

        {/* Back to home */}
        <div className="mt-4 text-center">
          <Link 
            to="/" 
            className="text-white/50 hover:text-white/70 text-xs transition-colors"
          >
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
